import React, { PureComponent, useRef, useState, useEffect } from 'react';

import { easeCubic } from 'd3-ease';
import { lineString } from '@turf/helpers';
import update from 'immutability-helper';

import API from 'files/api.js';
import Appearance from 'styles/Appearance.js';
import ReactMapGL, { FlyToInterpolator, Layer, Marker, Source, WebMercatorViewport } from 'react-map-gl';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

export const GHSCoordinate = {
    latitude: 32.972660,
    longitude: -97.016320
}
export const Map = ({
    annotations, center, features, heatmap, isScrollEnabled, isRotationEnabled, isZoomEnabled, onAnnotationClick, overlays, radius, style, useShadows, utils
}) => {

    const mapRef = useRef(null);
    const [_overlays, _setOverlays] = useState([]);
    const [_annotations, _setAnnotations] = useState([]);

    const [hover, setHover] = useState(null);
    const [mapStyle, setMapStyle] = useState('standard');
    const [showUserLocation, setShowUserLocation] = useState(false);
    const [viewport, setViewport] = useState({
        zoom: 12,
        latitude: center ? center.latitude : GHSCoordinate.latitude,
        longitude: center ? center.longitude : GHSCoordinate.longitude
    });

    const setFeatureProps = () => {
        
        if(!features || !mapRef.current) {
            return
        }

        // load icon from features object if applicable
        let { icons } = features;
        if(icons) {
            let map = mapRef.current.getMap();
            icons.forEach(icon => {
                map.loadImage(icon.path, (error, image) => {
                    if(error) {
                        console.log(error.message);
                        return;
                    }
                    if(!map.hasImage(icon.key)) {
                        map.addImage(icon.key, image, { sdf: true });
                    }
                });
            })
        }

        // set region based on points
        if(features.region) {

            // fit map bounds using supplied region
            const { longitude, latitude, zoom } = getRegion(features.region);
            setViewport(viewport => update(viewport, {
                ...getViewportAnimations,
                longitude: {
                    $set: isNaN(longitude) ? viewport.longitude : longitude
                },
                latitude: {
                    $set: isNaN(latitude) ? viewport.latitude : latitude
                },
                zoom: {
                    $set: zoom
                }
            }));
        }
    }

    const onHoverChange = hover_event => {

        let props = hover_event.features;
        let { srcEvent: { offsetX, offsetY } } = hover_event;
        let selected = props && props[0];

        if(!selected || !features) {
            setHover(null);
            return;
        }

        let { id, onHover } = features;
        if(id === selected.source && typeof(onHover) === 'function') {
            setHover({
                ...onHover(selected),
                x: offsetX + 8,
                y: offsetY + 8
            });
            return;
        }
        setHover(null);
    }

    const onMapClick = hover_event => {

        let props = hover_event.features;
        let { srcEvent: { offsetX, offsetY } } = hover_event;
        let selected = props && props[0];

        if(!selected || !features) {
            return;
        }

        let { id, onClick, onHover } = features;
        if(id === selected.source) {
            if(typeof(onHover) === 'function') {
                setHover({
                    ...onHover(selected),
                    loading: true, // update loading props
                    x: offsetX,
                    y: offsetY
                })
            }
            if(typeof(onClick) === 'function') {
                onClick(selected.properties);
            }
        }
    }

    const onZoom = amount => {
        let zoom = viewport.zoom + amount;
        setViewport(viewport => update(viewport, {
            ...getViewportAnimations,
            zoom: {
                $set: parseInt(zoom <= 0 ? 0 : (zoom > 25 ? 25 : zoom))
            }
        }));
    }

    const getAnnotations = () => {
        return (
            <Annotations
            data={_annotations}
            onClick={onAnnotationClick} />
        )
    }

    const getFeatureCollections = () => {
        if(!features || !features.layer || !features.data) {
            return null;
        }
        return (
            <Source
            id={features.id}
            type={'geojson'}
            data={features.data}>
                <Layer
                id={features.id}
                source={features.id}
                {...features.layer} />
            </Source>
        )
    }

    const getHeatMapLayer = () => {
        if(!heatmap) {
            return null;
        }
        let { data, layer } = heatmap;
        if(!data || !layer) {
            return null;
        }
        return (
            <Source
            type={'geojson'}
            data={data}>
                <Layer {...layer} />
            </Source>
        )
    }

    const getHover = () => {
        if(!hover || !hover.title) {
            return null;
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                left: hover.x,
                minWidth: 200,
                position: 'absolute',
                top: hover.y
            }}>
                {Views.entry({
                    ...hover,
                    bottomBorder: false,
                    hideIcon: hover.icon ? false : true
                })}
            </div>
        )
    }

    const getMapStyle = () => {
        if(mapStyle === 'satelite') {
            return 'mapbox://styles/llifewipe/cmlzb8ux6000s01qz0jlgg9kj';
        }
        if(window.theme === 'dark') {
            return 'mapbox://styles/llifewipe/cmfy9zvzb00dr01pd7uoq02dn';
        }
        return 'mapbox://styles/llifewipe/cmlze80nw004h01s3f532bqcs';
    }

    const getOverlays = () => {
        return _overlays.map((polyline, index) => {
            return (
                <Source
                key={polyline.key}
                type={'geojson'}
                data={polyline.coordinates}>
                    <Layer
                    id={polyline.key}
                    type={'line'}
                    source={'route'}
                    layout={{
                        'line-join': 'round',
                        'line-cap': 'round'
                    }}
                    paint={{
                        'line-color': polyline.color ? Appearance.colors.blue : Appearance.colors.grey(),
                        'line-width': 3
                    }}/>
                </Source>
            )
        })
    }

    const getRegion = region => {
        try {
            return new WebMercatorViewport(viewport).fitBounds(region, { padding: 50 });
        } catch(e) {
            // error may be thrown if padding exceeds viewport size
            console.error(e.message);
            return new WebMercatorViewport(viewport).fitBounds(region);
        }
    };

    const getViewportAnimations = {
        transitionDuration: {
            $set: 750
        },
        transitionInterpolator: {
            $set: new FlyToInterpolator()
        },
        transitionEasing: {
            $set: easeCubic
        }
    }

    useEffect(() => {
        setTimeout(setFeatureProps, 0);
    }, [features, mapStyle, mapRef.current]);

    // overlays
    useEffect(() => {
        _setOverlays(overlays && Array.isArray(overlays) ? overlays.map(overlay => {
            return {
                ...overlay,
                coordinates: lineString(overlay.coordinates.map(o => [ o[1], o[0] ]))
            }
        }) : []);
    }, [overlays]);

    useEffect(() => {

        // fit into viewport
        if(_overlays.length > 0) {
            let region = Utils.getRegionFromAnnotations(overlays.reduce((array, overlay) => {
                return array.concat(overlay.coordinates)
            }, []));

            const { longitude, latitude, zoom } = getRegion(region);
            setViewport(viewport => update(viewport, {
                ...getViewportAnimations,
                longitude: {
                    $set: isNaN(longitude) ? viewport.longitude : longitude
                },
                latitude: {
                    $set: isNaN(latitude) ? viewport.latitude : latitude
                },
                zoom: {
                    $set: zoom
                }
            }));
        }

    }, [_overlays]);

    // Annotations
    useEffect(() => {
        if(!annotations && !Array.isArray(annotations)) {
            return;
        }

        let newAnnotations = annotations.filter(a => a.location && a.location.latitude && a.location.longitude);
        if(showUserLocation && window.userLocation) {
            newAnnotations.push({
                id: 'current-location',
                title: 'Current Location',
                location: {
                    latitude: window.userLocation.latitude,
                    longitude: window.userLocation.longitude
                }
            });
        }
        _setAnnotations(newAnnotations);

    }, [annotations, showUserLocation]);

    useEffect(() => {

        if(_annotations.length === 0) {
            return;
        }

        // single annotation
        if(_annotations.length === 1) {
            setViewport(viewport => update(viewport, {
                ...getViewportAnimations,
                longitude: {
                    $set: _annotations[0].location.longitude
                },
                latitude: {
                    $set: _annotations[0].location.latitude
                },
                zoom: {
                    $set: 16
                }
            }));
            return;
        }

        // multiple annotations
        const { longitude, latitude, zoom } = getRegion(Utils.getRegionFromAnnotations(_annotations));
        setViewport(viewport => update(viewport, {
            ...getViewportAnimations,
            longitude: {
                $set: isNaN(longitude) ? viewport.longitude : longitude
            },
            latitude: {
                $set: isNaN(latitude) ? viewport.latitude : latitude
            },
            zoom: {
                $set: _annotations.length === 1 || zoom >= 20 ? 18 : zoom
            }
        }));

    }, [_annotations, annotations ]);

    return (
        <div style={{
            borderRadius: 10,
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            ...style
        }}>
            <ReactMapGL
            ref={mapRef}
            {...viewport}
            attributionControl={false}
            doubleClickZoom={isZoomEnabled || false}
            dragPan={isScrollEnabled || false}
            dragRotate={isRotationEnabled || false}
            height={style && style.height ? style.height : '100%'}
            mapboxApiAccessToken={API.mapbox}
            mapStyle={getMapStyle()}
            onClick={onMapClick}
            onHover={onHoverChange}
            onViewportChange={setViewport}
            scrollZoom={false}
            touchRotate={isRotationEnabled || false}
            touchZoom={isZoomEnabled || false}
            width={style && style.width ? style.width : '100%'}>
                {getAnnotations()}
                {getOverlays()}
                {getHeatMapLayer()}
                {getFeatureCollections()}
                {getHover()}
            </ReactMapGL>

            <div style={{
                alignItems: 'center',
                bottom: 8,
                display: 'flex',
                flexDirection: 'row',
                position: 'absolute',
                right: 8
            }}>
                <div style={{
                    backgroundColor: window.theme === 'dark' ? 'rgba(45,45,45,1)' : 'white',
                    border: `1px solid ${Appearance.colors.divider()}`,
                    borderRadius: 5
                }}>
                    <span
                    className={'d-none text-button'}
                    onClick={() => setMapStyle(style => style === 'satelite' ? 'standard' : 'satelite')}
                    style={{
                        ...Appearance.textStyles.subTitle(),
                        borderRight: `1px solid ${Appearance.colors.divider()}`,
                        padding: '8px 12px 8px 12px'
                    }}>{mapStyle === 'satelite' ? 'Standard' : 'Satelite'}</span>
                    <img
                    className={'text-button'}
                    src={'images/minus-icon-grey-small.png'}
                    onClick={onZoom.bind(this, -1)}
                    style={{
                        borderRight: '1px solid rgba(174,174,174,0.15)',
                        height: 25,
                        objectFit: 'contain',
                        opacity: 0.75,
                        padding: 8,
                        width: 25
                    }} />
                    <img
                    className={'text-button'}
                    src={'images/plus-icon-grey-small.png'}
                    onClick={onZoom.bind(this, 1)}
                    style={{
                        height: 25,
                        objectFit: 'contain',
                        opacity: 0.75,
                        padding: 8,
                        width: 25
                    }} />
                </div>
            </div>
        </div>
    );
}

class Annotations extends PureComponent {

    state = {
        hover: false
    }

    getMarkerIcon = annotation => {

        let { icon } = annotation;
        let { onClick } = this.props;

        if(icon && icon.component) {
            return (
                <div
                className={onClick ? 'image-button' : ''}
                onMouseEnter={() => this.setState({ hover: annotation.id })}
                onMouseLeave={() => this.setState({ hover: null })}
                onClick={onClick ? onClick.bind(this, annotation.id) : null}>
                    {icon.component}
                </div>
            );
        }

        return (
            <img
            className={onClick ? 'image-button' : ''}
            onMouseEnter={() => this.setState({ hover: annotation.id })}
            onMouseLeave={() => this.setState({ hover: null })}
            onClick={onClick ? onClick.bind(this, annotation.id) : null}
            src={icon && icon.path ? icon.path : 'images/location-icon-grey.png'}
            style={{
                width: 35,
                height: 35,
                objectFit: 'contain',
                ...(icon ? icon.style : null)
            }} />
        )
    }

    render() {

        const { data } = this.props;
        if(!data) {
            return null;
        }
        return data.map((annotation, index) => {

            if(!annotation.location) {
                return null;
            }
            if(annotation.id === 'current-location') {
                return (
                    <Marker
                    key={index}
                    longitude={annotation.location.longitude}
                    latitude={annotation.location.latitude}
                    offsetTop={-9}
                    offsetLeft={-9}>
                        <div
                        className={'current-location'}
                        style={{
                            width: 18,
                            height: 18
                        }} />
                    </Marker>
                )
            }
            return (
                <Marker
                key={index}
                captureClick={false}
                offsetTop={-35}
                offsetLeft={-17.5}
                {...annotation.location}
                {...annotation.icon && annotation.icon.style ? annotation.icon.style.offset : null}>
                    <div style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        {this.getMarkerIcon(annotation)}
                        {this.state.hover === annotation.id && typeof(annotation.title) === 'string' && (
                            <div style={{
                                position: 'absolute',
                                display: 'flex',
                                flexDirection: 'column',
                                top: 40,
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.softBorder()}`,
                                padding: '6px 12px 6px 12px',
                                borderRadius: 10,
                                overflow: 'hidden',
                                textAlign: 'center',
                                zIndex: 4500
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    marginBottom: 2
                                }}>{annotation.title}</span>
                                {typeof(annotation.subTitle) === 'string' && (
                                    <span style={{
                                        ...Appearance.textStyles.subTitle(),
                                        marginBottom: 2
                                    }}>{annotation.subTitle}</span>
                                )}
                            </div>
                        )}
                    </div>
                </Marker>
            )
        })
    }
}
